import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCog } from 'react-icons/fa';
import U2000ActionBtn from '../../u2000Core/components/buttons/U2000ActionBtn';
import U2000Panel from '../../u2000Core/components/layout/U2000Panel';
import { NS } from '../u340xKpiNs';
import type { U340xKpiTileProps } from '../types/u340xKpiTypes';
import { U340X_KPI_MIN_MONTHS } from '../types/u340xKpiTypes';
import { u340xTimelineInset } from '../theme/u340xEchartsTheme';
import U340xKpiDualAxisChart from './U340xKpiDualAxisChart';
import U340xKpiMonthsConfigDialog from './U340xKpiMonthsConfigDialog';

function U340xKpiTile({
  title,
  size = 'large',
  series,
  primaryMetric,
  secondaryMetric,
  primaryStackMetric,
  secondaryExtraMetric,
  secondaryTecMetric,
  secondaryTecExtraMetric,
  months,
  maxMonths,
  minMonths = U340X_KPI_MIN_MONTHS,
  status,
  errorMessage,
  onMonthsChange,
  onMonthClick,
  selectedMonth,
  headerExtra,
  timeline,
  showMonthsConfig,
}: U340xKpiTileProps) {
  const { t } = useTranslation(NS);
  const [configOpen, setConfigOpen] = useState(false);

  const showEmpty = status === 'empty' || (status === 'ready' && series.length === 0);
  const showChart = status === 'ready' && series.length > 0;
  const monthsInHeader =
    Boolean(onMonthsChange) && (showMonthsConfig ?? true);
  const showHeaderRow = Boolean(headerExtra) || monthsInHeader;

  return (
    <>
      <U2000Panel
        title={title}
        variant="outlined"
        className={`pt-6! ${size === 'large' ? 'min-h-88' : ''}`}
      >
        {showHeaderRow ? (
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">{headerExtra}</div>
            {monthsInHeader ? (
              <U2000ActionBtn
                label={t('config.open')}
                icon={FaCog}
                variant="secondary"
                className="px-3! py-1.5! text-xs"
                title={t('config.openTitle', { months })}
                handleClick={() => setConfigOpen(true)}
              />
            ) : null}
          </div>
        ) : null}

        {timeline ? (
          <div
            className="mb-4"
            style={{
              paddingLeft: u340xTimelineInset.leftPx,
              paddingRight: u340xTimelineInset.rightPx,
            }}
          >
            {timeline}
          </div>
        ) : null}

        <div className="min-h-64">
          {status === 'loading' ? (
            <div
              className="flex h-64 flex-col justify-center gap-3 px-2"
              aria-busy="true"
              aria-label={t('status.loading')}
            >
              <div className="h-4 w-1/3 animate-pulse rounded-sm bg-gray-200" />
              <div className="h-40 w-full animate-pulse rounded-sm bg-gray-100" />
              <div className="h-3 w-1/2 animate-pulse rounded-sm bg-gray-200" />
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="flex h-64 flex-col items-center justify-center gap-2 px-4 text-center">
              <p className="text-sm font-medium text-red-700">{t('status.error')}</p>
              <p className="text-sm text-muted-foreground">
                {errorMessage ?? t('status.errorDefault')}
              </p>
            </div>
          ) : null}

          {showEmpty ? (
            <div className="flex h-64 items-center justify-center px-4 text-center">
              <p className="text-sm text-muted-foreground">{t('status.empty')}</p>
            </div>
          ) : null}

          {showChart ? (
            <U340xKpiDualAxisChart
              series={series}
              primaryMetric={primaryMetric}
              secondaryMetric={secondaryMetric}
              primaryStackMetric={primaryStackMetric}
              secondaryExtraMetric={secondaryExtraMetric}
              secondaryTecMetric={secondaryTecMetric}
              secondaryTecExtraMetric={secondaryTecExtraMetric}
              onMonthClick={onMonthClick}
              selectedMonth={selectedMonth}
            />
          ) : null}
        </div>
      </U2000Panel>

      {monthsInHeader && onMonthsChange ? (
        <U340xKpiMonthsConfigDialog
          isOpen={configOpen}
          months={months}
          maxMonths={maxMonths}
          minMonths={minMonths}
          onClose={() => setConfigOpen(false)}
          onSave={onMonthsChange}
        />
      ) : null}
    </>
  );
}

export default U340xKpiTile;
